import type { GridCell, GridEdgeRef, GridVertex } from "./gridMeshTypes";
import { distanceSq, type BoundaryPoint } from "./geo";

/**
 * Near-zero cutoff for degenerate segment lengths inside colinearity helpers
 * (e.g. treat a vanishing edge as trivially colinear). Not for slide/rail math.
 *
 * To allow "almost colinear" pruning, loosen COLINEAR_SIN_EPSILON — do not
 * widen this and reuse it as a direction-length guard elsewhere.
 */
export const COLINEAR_EPSILON = 1e-12;

/**
 * Near-zero length for degenerate direction / distance guards (independent of
 * colinearity). Keep separate so pruning tolerance can change without treating
 * short-but-valid slide rails as empty.
 */
export const NEAR_ZERO_LENGTH_EPSILON = 1e-12;

/**
 * Max |sin(θ)| to treat two directions as colinear. Scale-invariant so
 * meter-scale plot edges are not treated as colinear with their perpendiculars
 * (an absolute cross-product epsilon is). ~0.0057°.
 */
const COLINEAR_SIN_EPSILON = 1e-4;

/**
 * Project `point` onto the infinite line through lineStart→lineEnd (unclamped
 * parametric projection). Degenerate zero-length lines return lineStart.
 */
export const projectPointOntoInfiniteLine = (
  point: BoundaryPoint,
  lineStart: BoundaryPoint,
  lineEnd: BoundaryPoint,
): BoundaryPoint => {
  const deltaLongitude = lineEnd.longitude - lineStart.longitude;
  const deltaLatitude = lineEnd.latitude - lineStart.latitude;
  const segmentLengthSq = distanceSq(lineStart, lineEnd);

  if (segmentLengthSq === 0) {
    return lineStart;
  }

  // projection = dot(point - start, direction) / |direction|² (unclamped).
  const projection =
    ((point.longitude - lineStart.longitude) * deltaLongitude +
      (point.latitude - lineStart.latitude) * deltaLatitude) /
    segmentLengthSq;

  return {
    latitude: lineStart.latitude + projection * deltaLatitude,
    longitude: lineStart.longitude + projection * deltaLongitude,
  };
};

/** Project onto the finite segment (clamped to endpoints). */
export const projectPointOntoSegment = (
  point: BoundaryPoint,
  lineStart: BoundaryPoint,
  lineEnd: BoundaryPoint,
): BoundaryPoint => {
  const deltaLongitude = lineEnd.longitude - lineStart.longitude;
  const deltaLatitude = lineEnd.latitude - lineStart.latitude;
  const segmentLengthSq = distanceSq(lineStart, lineEnd);

  if (segmentLengthSq === 0) {
    return lineStart;
  }

  // Same parametric projection as the infinite-line project, clamped to [0, 1].
  const projection = Math.max(
    0,
    Math.min(
      1,
      ((point.longitude - lineStart.longitude) * deltaLongitude +
        (point.latitude - lineStart.latitude) * deltaLatitude) /
        segmentLengthSq,
    ),
  );

  return {
    latitude: lineStart.latitude + projection * deltaLatitude,
    longitude: lineStart.longitude + projection * deltaLongitude,
  };
};

/** True when |sin(θ)| between (ax,ay) and (bx,by) is within COLINEAR_SIN_EPSILON. */
const isCrossProductNegligible = (
  ax: number,
  ay: number,
  bx: number,
  by: number,
): boolean => {
  const aLen = Math.hypot(ax, ay);
  const bLen = Math.hypot(bx, by);
  if (aLen < COLINEAR_EPSILON || bLen < COLINEAR_EPSILON) {
    return true;
  }
  return Math.abs(ax * by - ay * bx) <= COLINEAR_SIN_EPSILON * aLen * bLen;
};

/**
 * True when segment a→b is colinear with segment c→d (same supporting line).
 *
 * Two checks (equivalent to requiring both c and d on line a→b):
 * 1. ab × cd ≈ 0 — directions are parallel. Alone this still allows parallel but
 *    distinct lines (e.g. two horizontal edges on different rows).
 * 2. ab × ac ≈ 0 — point c lies on the infinite line through a→b. With (1), d
 *    must lie on that line too.
 */
export const areColinear = (
  a: BoundaryPoint,
  b: BoundaryPoint,
  c: BoundaryPoint,
  d: BoundaryPoint,
): boolean => {
  const abx = b.longitude - a.longitude;
  const aby = b.latitude - a.latitude;
  const cdx = d.longitude - c.longitude;
  const cdy = d.latitude - c.latitude;
  // (1) Parallel directions.
  if (!isCrossProductNegligible(abx, aby, cdx, cdy)) {
    return false;
  }

  // (2) Same supporting line (c on line a→b; d follows from parallel).
  const acx = c.longitude - a.longitude;
  const acy = c.latitude - a.latitude;
  return isCrossProductNegligible(abx, aby, acx, acy);
};

/** True when mid lies on the line through prev→next (redundant along a straight edge). */
export const isColinearVertex = (
  prev: BoundaryPoint,
  mid: BoundaryPoint,
  next: BoundaryPoint,
): boolean => {
  const abx = mid.longitude - prev.longitude;
  const aby = mid.latitude - prev.latitude;
  const bcx = next.longitude - mid.longitude;
  const bcy = next.latitude - mid.latitude;
  const abLen = Math.hypot(abx, aby);
  const bcLen = Math.hypot(bcx, bcy);
  if (abLen < COLINEAR_EPSILON || bcLen < COLINEAR_EPSILON) {
    return true;
  }
  return isCrossProductNegligible(abx, aby, bcx, bcy);
};

/** True when every point lies on the line through the polyline's first and last points. */
export const polylineIsStraight = (points: BoundaryPoint[]): boolean => {
  if (points.length <= 2) {
    return true;
  }
  const first = points[0];
  const last = points[points.length - 1];
  const abx = last.longitude - first.longitude;
  const aby = last.latitude - first.latitude;
  for (let index = 1; index < points.length - 1; index += 1) {
    const mid = points[index];
    const acx = mid.longitude - first.longitude;
    const acy = mid.latitude - first.latitude;
    if (!isCrossProductNegligible(abx, aby, acx, acy)) {
      return false;
    }
  }
  return true;
};

export const canonicalizeEdge = (
  startVertexId: string,
  endVertexId: string,
): GridEdgeRef =>
  startVertexId < endVertexId
    ? { endVertexId, startVertexId }
    : { endVertexId: startVertexId, startVertexId: endVertexId };

export const edgeKey = (startVertexId: string, endVertexId: string): string => {
  const edge = canonicalizeEdge(startVertexId, endVertexId);
  return `${edge.startVertexId}|${edge.endVertexId}`;
};

export const getVertexPoint = (
  vertices: Record<string, GridVertex>,
  id: string,
): BoundaryPoint | null => {
  const vertex = vertices[id];
  if (vertex == null) {
    return null;
  }

  return { latitude: vertex.latitude, longitude: vertex.longitude };
};

/** First vertex within NEAR_ZERO_LENGTH_EPSILON of `point`, or null. */
export const findSnappedVertexId = (
  vertices: Record<string, GridVertex>,
  point: BoundaryPoint,
): string | null => {
  for (const vertex of Object.values(vertices)) {
    if (
      distanceSq(
        { latitude: vertex.latitude, longitude: vertex.longitude },
        point,
      ) <= NEAR_ZERO_LENGTH_EPSILON
    ) {
      return vertex.id;
    }
  }

  return null;
};

export const boundaryRing = (
  vertexIds: readonly string[],
  vertices: Record<string, GridVertex>,
): BoundaryPoint[] => {
  const ring: BoundaryPoint[] = [];
  vertexIds.forEach((vertexId) => {
    const point = getVertexPoint(vertices, vertexId);
    if (point == null) {
      return;
    }
    ring.push(point);
  });
  return ring;
};

export const toRadians = (degrees: number): number => (degrees * Math.PI) / 180;

export const toDegrees = (radians: number): number => (radians * 180) / Math.PI;

const ringExtent = (
  ring: readonly BoundaryPoint[],
  origin: BoundaryPoint,
  lngScale: number,
  ux: number,
  uy: number,
): { maxT: number; minT: number } => {
  let minT = Number.POSITIVE_INFINITY;
  let maxT = Number.NEGATIVE_INFINITY;
  ring.forEach((point) => {
    const x = (point.longitude - origin.longitude) * lngScale;
    const y = point.latitude - origin.latitude;
    const t = x * ux + y * uy;
    minT = Math.min(minT, t);
    maxT = Math.max(maxT, t);
  });
  return { maxT, minT };
};

/**
 * Centerline through the ring centroid, parallel to the side the ring
 * extends farthest along. A subdivided long edge still wins over one short
 * end. The line points east, or north when that side is vertical, so a
 * map-aligned label reads upright on a north-up map.
 */
export const rowLongSideCenterline = (
  ring: readonly BoundaryPoint[],
): [BoundaryPoint, BoundaryPoint] | null => {
  if (ring.length < 2) {
    return null;
  }

  let sumLat = 0;
  let sumLng = 0;
  ring.forEach((point) => {
    sumLat += point.latitude;
    sumLng += point.longitude;
  });
  const origin = {
    latitude: sumLat / ring.length,
    longitude: sumLng / ring.length,
  };
  const lngScale = Math.cos(toRadians(origin.latitude)) || 1;

  let bestExtent = 0;
  let ux = 0;
  let uy = 0;
  for (let index = 0; index < ring.length; index += 1) {
    const start = ring[index];
    const end = ring[(index + 1) % ring.length];
    if (start == null || end == null) {
      continue;
    }
    const x = (end.longitude - start.longitude) * lngScale;
    const y = end.latitude - start.latitude;
    const length = Math.hypot(x, y);
    if (length <= NEAR_ZERO_LENGTH_EPSILON) {
      continue;
    }
    const candidateUx = x / length;
    const candidateUy = y / length;
    const { maxT, minT } = ringExtent(
      ring,
      origin,
      lngScale,
      candidateUx,
      candidateUy,
    );
    const extent = maxT - minT;
    if (extent > bestExtent) {
      bestExtent = extent;
      ux = candidateUx;
      uy = candidateUy;
    }
  }
  if (bestExtent <= NEAR_ZERO_LENGTH_EPSILON) {
    return null;
  }

  const { maxT, minT } = ringExtent(ring, origin, lngScale, ux, uy);
  const pointAt = (t: number): BoundaryPoint => ({
    latitude: origin.latitude + uy * t,
    longitude: origin.longitude + (ux * t) / lngScale,
  });
  const start = pointAt(minT);
  const end = pointAt(maxT);
  const east = (end.longitude - start.longitude) * lngScale;
  const north = end.latitude - start.latitude;
  const bearing = toDegrees(Math.atan2(east, north));
  if (bearing < 0 || bearing >= 180) {
    return [end, start];
  }
  return [start, end];
};

/** Average of ring vertices as a geo point; used for label placement. */
export const cellCentroid = (
  cell: GridCell,
  vertices: Record<string, GridVertex>,
): BoundaryPoint | null => {
  const ring = boundaryRing(cell.vertexIds, vertices);
  if (ring.length === 0) {
    return null;
  }

  let sumLng = 0;
  let sumLat = 0;
  ring.forEach((point) => {
    sumLng += point.longitude;
    sumLat += point.latitude;
  });

  return {
    latitude: sumLat / ring.length,
    longitude: sumLng / ring.length,
  };
};

export const collectUniqueEdges = (cells: GridCell[]): GridEdgeRef[] => {
  const seen = new Set<string>();
  const edges: GridEdgeRef[] = [];

  cells.forEach((cell) => {
    const count = cell.vertexIds.length;
    for (let index = 0; index < count; index += 1) {
      const startVertexId = cell.vertexIds[index];
      const endVertexId = cell.vertexIds[(index + 1) % count];
      if (startVertexId == null || endVertexId == null) {
        continue;
      }
      const key = edgeKey(startVertexId, endVertexId);
      if (seen.has(key)) {
        continue;
      }
      seen.add(key);
      edges.push(canonicalizeEdge(startVertexId, endVertexId));
    }
  });

  return edges;
};

export const edgesShareEndpoint = (a: GridEdgeRef, b: GridEdgeRef): boolean =>
  a.startVertexId === b.startVertexId ||
  a.startVertexId === b.endVertexId ||
  a.endVertexId === b.startVertexId ||
  a.endVertexId === b.endVertexId;
